// The admin analytics, today or over the last 7, 30 or 90 days: headline numbers against the window
// before, shares and challenges, players each day, per-row days against pattern days, every edition,
// retention by first-play day, hours, devices and the words played most. Today leaves out the two
// that need more than a day: players each day and retention. Built for desktop, where the admin page
// widens; on phones the sections stack and the wide tables scroll sideways.
import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  ANALYTICS_DAYS, RETENTION_OFFSETS, type AdminAnalytics, type AnalyticsDays, type AnalyticsKpis, type SharingCounts,
  type WordCount,
} from '@wbwl/contract'
import { editionDate, PATTERN_DAYS_FROM, RANK_STEPS } from '@wbwl/rules'
import { ErrorNote } from './shell.tsx'
import { useAdminAnalytics } from '../lib/data.ts'
import { dayDate, fmt } from '../lib/game.ts'

export function Analytics() {
  const [days, setDays] = useState<AnalyticsDays>(30)
  const res = useAdminAnalytics(days)
  // Switching windows keeps the last one on screen, dimmed, until the new one arrives.
  const last = useRef<AdminAnalytics | null>(null)
  if (res.data) last.current = res.data
  const a = res.data ?? last.current
  return (
    <section className="an">
      <div className="an-head">
        <h3>Analytics</h3>
        <WindowTabs days={days} set={setDays} />
        {a && (
          <span className="faint small an-span">
            {a.days === 1 ? 'Today so far · changes are against yesterday up to this time'
              : `${dayDate(a.since)} to today so far · changes are against the ${a.days} days before, up to this time of day`}
          </span>
        )}
      </div>
      {res.error && !res.data ? <ErrorNote error={res.error} retry={() => res.reload()} />
        : !a ? <p className="faint empty-note">Counting…</p>
        : (
          <div className={`an-body${res.data ? '' : ' stale'}`}>
            <Kpis cur={a.kpis.current} prev={a.kpis.previous} days={a.days} />
            <div className="an-grid">
              <Card title="Sharing" sub="results shared from the share sheet, and the visits their links bring">
                <Counts a={a} rows={SHARE_ROWS} />
                <p className="faint small an-note">A share is one share sheet that sent something: its link (Send or Copy link), or on the old share sheet its emoji grid or image. Links open the sharer’s challenge. Visits leave out the sharer’s own and link-preview bots, and count once a day per link per visitor. All time starts when links went into the share text.</p>
              </Card>
              <Card title="Challenges" sub="links made, and friends who took them up">
                <Counts a={a} rows={CHALLENGE_ROWS} />
                <p className="faint small an-note">Accepted: joined someone else’s challenge, by its link or an in-app invite. Finished: of those, played its edition to the end.</p>
              </Card>
            </div>
            {a.days > 1 && (
              <Card title="Players each day" sub="who played the day’s edition on its day">
                <Daily a={a} />
              </Card>
            )}
            <div className="an-grid">
              <Card title="Per-row and pattern days" sub="games played on their edition’s day">
                <Layouts a={a} />
              </Card>
              <div className="an-stack">
                <Card title="Ranks reached" sub="games finished on their edition’s day">
                  <Ranks a={a} />
                </Card>
                <Card title="Devices" sub="active players, by their latest session">
                  <Devices a={a} />
                </Card>
              </div>
            </div>
            <Card title="Editions" sub="newest first">
              <Editions a={a} />
            </Card>
            {a.days === 1 ? (
              <Card title="When people play" sub="games started each hour, Pacific">
                <Hours a={a} />
              </Card>
            ) : (
              <div className="an-grid">
                <Card title="Coming back" sub="by the day of their first game: the share who played again exactly N days later">
                  <Retention a={a} />
                </Card>
                <Card title="When people play" sub="games started each hour, Pacific">
                  <Hours a={a} />
                </Card>
              </div>
            )}
            <Card title="Words played most" sub="in games started in the window">
              <Words a={a} />
            </Card>
          </div>
        )}
    </section>
  )
}

/** The window picker, shared with the Slow Burn tab. */
export function WindowTabs({ days, set }: { days: AnalyticsDays; set(days: AnalyticsDays): void }) {
  return (
    <div className="seg an-range" role="tablist" aria-label="Window">
      {ANALYTICS_DAYS.map((d) => (
        <button key={d} type="button" role="tab" aria-selected={d === days} className={d === days ? 'on' : ''} onClick={() => set(d)}>{d === 1 ? 'Today' : `${d} days`}</button>
      ))}
    </div>
  )
}

export function Card({ title, sub, children }: { title: string; sub?: string; children: ReactNode }) {
  return (
    <div className="an-card">
      <div className="an-card-head"><h4>{title}</h4>{sub && <span className="faint small">{sub}</span>}</div>
      {children}
    </div>
  )
}

// ---------- numbers ----------

const ratio = (n: number, of: number) => (of ? n / of : null)
const pct = (x: number | null) => (x === null ? '—' : `${Math.round(x * 100)}%`)

/** Signed change from `prev`: a percentage for counts, points for shares. */
function Delta({ cur, prev, points = false }: { cur: number | null; prev: number | null; points?: boolean }) {
  if (cur === null || prev === null) return <span className="dl">—</span>
  let d: number
  let text: string
  if (points) {
    d = Math.round((cur - prev) * 100)
    text = `${Math.abs(d)} pt${Math.abs(d) === 1 ? '' : 's'}`
  } else if (prev === 0) {
    return <span className="dl">{cur > 0 ? 'none before' : 'no change'}</span>
  } else {
    d = Math.round(((cur - prev) / prev) * 100)
    text = `${Math.abs(d)}%`
  }
  if (d === 0) return <span className="dl">no change</span>
  return <span className={`dl ${d > 0 ? 'up' : 'down'}`}>{d > 0 ? '▲' : '▼'} {text}</span>
}

function Kpis({ cur, prev, days }: { cur: AnalyticsKpis; prev: AnalyticsKpis; days: number }) {
  const today = days === 1
  const tiles: { label: string; value: string; delta: ReactNode; note: string }[] = [
    { label: 'Active players', value: fmt(cur.activePlayers), delta: <Delta cur={cur.activePlayers} prev={prev.activePlayers} />, note: 'started any puzzle' },
    today
      ? { label: 'Players', value: fmt(cur.started), delta: <Delta cur={cur.started} prev={prev.started} />, note: 'on today’s edition' }
      : { label: 'Players a day', value: (cur.started / days).toFixed(1), delta: <Delta cur={cur.started} prev={prev.started} />, note: 'average, on the day’s edition' },
    { label: 'Finish rate', value: pct(ratio(cur.finished, cur.started)), delta: <Delta cur={ratio(cur.finished, cur.started)} prev={ratio(prev.finished, prev.started)} points />, note: `${fmt(cur.finished)} of ${fmt(cur.started)} finished on the day` },
    {
      label: today ? 'Back today' : 'Back the next day',
      value: pct(ratio(cur.nextDay, cur.firstPlays)),
      delta: <Delta cur={ratio(cur.nextDay, cur.firstPlays)} prev={ratio(prev.nextDay, prev.firstPlays)} points />,
      note: `${fmt(cur.nextDay)} of ${fmt(cur.firstPlays)} ${today ? 'of yesterday’s first-timers' : 'first-timers'}`,
    },
    { label: 'New players', value: fmt(cur.newPlayed), delta: <Delta cur={cur.newPlayed} prev={prev.newPlayed} />, note: `played a word, of ${fmt(cur.newPlayers)} new visitors` },
    { label: 'Sign-ups', value: fmt(cur.signUps), delta: <Delta cur={cur.signUps} prev={prev.signUps} />, note: 'made an account' },
    { label: 'Median score', value: pct(cur.medianShare), delta: <Delta cur={cur.medianShare} prev={prev.medianShare} points />, note: 'of the solver’s best' },
    { label: 'Comments', value: fmt(cur.comments), delta: <Delta cur={cur.comments} prev={prev.comments} />, note: 'in edition discussions' },
  ]
  return (
    <div className="an-kpis">
      {tiles.map((t) => (
        <div className="kpi" key={t.label}>
          <span className="kl">{t.label}</span>
          <b>{t.value}</b>
          <span className="ft">{t.delta}<span className="nt">{t.note}</span></span>
        </div>
      ))}
    </div>
  )
}

// ---------- shares and challenges ----------

interface CountRow {
  readonly label: string
  readonly n: (s: SharingCounts) => number
  /** Shown as a share of this, when there is one. */
  readonly of?: (s: SharingCounts) => number
  readonly head?: boolean
}

const SHARE_ROWS: readonly CountRow[] = [
  { label: 'Results shared', n: (s) => s.shares, head: true },
  { label: 'Link sent', n: (s) => s.sent },
  { label: 'Emoji grid copied', n: (s) => s.copied },
  { label: 'Image saved', n: (s) => s.saved },
  { label: 'Links clicked', n: (s) => s.linksClicked, of: (s) => s.linked },
  { label: 'Visits', n: (s) => s.clicks },
  { label: 'New visitors', n: (s) => s.newPlayers, of: (s) => s.clicks },
]

const CHALLENGE_ROWS: readonly CountRow[] = [
  { label: 'Challenges made', n: (s) => s.challenges, head: true },
  { label: 'Accepted', n: (s) => s.accepted },
  { label: 'Finished', n: (s) => s.finished, of: (s) => s.accepted },
]

/** The window, its change from the days before, and all time. */
function Counts({ a, rows }: { a: AdminAnalytics; rows: readonly CountRow[] }) {
  const { current, previous, allTime } = a.sharing
  const value = (s: SharingCounts, r: CountRow) => (r.of ? <Share n={r.n(s)} of={r.of(s)} /> : fmt(r.n(s)))
  return (
    <div className="an-table-wrap">
      <table className="an-table">
        <thead><tr><th /><th>{a.days === 1 ? 'Today' : <>Last {a.days}<br />days</>}</th><th>Change</th><th>All<br />time</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className={r.head ? 'head' : 'sub'}>
              <td>{r.head ? <b>{r.label}</b> : r.label}</td>
              <td>{value(current, r)}</td>
              <td><Delta cur={r.n(current)} prev={r.n(previous)} /></td>
              <td>{value(allTime, r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------- column chart ----------

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, width] as const
}

/** A round axis top for counts, at least 4, whose half is a whole number too. */
function niceMax(v: number): number {
  const top = Math.max(4, v)
  const p = 10 ** Math.floor(Math.log10(top))
  for (const m of [1, 1.2, 1.6, 2, 3, 4, 5, 6, 8, 10]) if (m * p >= top && Number.isInteger((m * p) / 2)) return m * p
  return 10 * p
}

/** A column from y0 (base) up to y1, its top corners rounded when it's the top of its stack. */
function colPath(x: number, w: number, y0: number, y1: number, round: boolean): string {
  const h = y0 - y1
  if (h <= 0) return ''
  const r = round ? Math.min(4, w / 2, h) : 0
  return `M${x},${y0}V${y1 + r}Q${x},${y1} ${x + r},${y1}H${x + w - r}Q${x + w},${y1} ${x + w},${y1 + r}V${y0}Z`
}

interface Column {
  readonly key: string
  /** Stacked values, bottom first, one per series. */
  readonly values: readonly number[]
  /** Under the axis; empty for none. */
  readonly label: string
  readonly marker?: boolean
  readonly tip: ReactNode
}

/** Stacked columns with a hover and focus tooltip per column. `series` are CSS colors, bottom first. */
function Columns({ cols, series, height = 200 }: { cols: readonly Column[]; series: readonly string[]; height?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)
  const top = 10, bottom = 34, left = 30, right = 4
  const max = niceMax(Math.max(...cols.map((c) => c.values.reduce((s, v) => s + v, 0))))
  const plotW = Math.max(0, width - left - right)
  const band = plotW / cols.length
  const barW = Math.max(2, Math.min(24, band * 0.68))
  const y = (v: number) => top + (height - top - bottom) * (1 - v / max)
  const ticks = Number.isInteger(max / 2) ? [0, max / 2, max] : [0, max]
  const tipX = hover === null ? 0 : left + band * (hover + 0.5)
  return (
    <div className="an-chart" ref={ref} onPointerLeave={() => setHover(null)}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Column chart; hover or focus a column for its numbers">
          <g className="grid">
            {ticks.map((t) => (
              <g key={t}>
                <line x1={left} x2={width - right} y1={y(t)} y2={y(t)} />
                <text x={left - 6} y={y(t) + 3.5} textAnchor="end">{fmt(t)}</text>
              </g>
            ))}
          </g>
          {cols.map((c, i) => {
            const x = left + band * i + (band - barW) / 2
            let base = 0
            const lastSeries = c.values.reduce((at, v, s) => (v > 0 ? s : at), -1)
            return (
              <g key={c.key}>
                {c.values.map((v, s) => {
                  if (v <= 0) return null
                  // A 2px gap of surface between stacked segments.
                  const y0 = y(base) - (base > 0 ? 2 : 0)
                  base += v
                  return <path key={s} d={colPath(x, barW, y0, y(base), s === lastSeries)} fill={series[s]} opacity={hover === null || hover === i ? 1 : 0.55} />
                })}
                {c.marker && <rect x={left + band * (i + 0.5) - 3} y={height - bottom + 6} width={6} height={6} transform={`rotate(45 ${left + band * (i + 0.5)} ${height - bottom + 9})`} className="mk" />}
                {c.label && <text x={left + band * (i + 0.5)} y={height - 6} textAnchor="middle">{c.label}</text>}
                <rect
                  className="hit" x={left + band * i} y={top} width={band} height={height - top - bottom + 18} tabIndex={0}
                  onPointerEnter={() => setHover(i)} onPointerMove={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                />
              </g>
            )
          })}
        </svg>
      )}
      {hover !== null && (
        <div className={`an-tip${tipX > width / 2 ? ' left' : ''}`} style={{ left: tipX }}>{cols[hover].tip}</div>
      )}
    </div>
  )
}

const Key = ({ color }: { color: string }) => <i className="key" style={{ background: color }} />
const TipRow = ({ color, value, label }: { color?: string; value: ReactNode; label: string }) => (
  <div className="tr">{color ? <Key color={color} /> : <i className="key none" />}<b>{value}</b><span>{label}</span></div>
)

const S1 = 'var(--s1)'
const S2 = 'var(--s2)'
const shortDay = (date: string) => new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })

function Daily({ a }: { a: AdminAnalytics }) {
  const every = a.daily.length <= 7 ? 1 : a.daily.length <= 30 ? 5 : 14
  const n = a.daily.length
  const cols: Column[] = a.daily.map((d, i) => ({
    key: d.date,
    values: [d.players - d.firstTimers, d.firstTimers],
    label: i === n - 1 ? 'today' : (n - 1 - i) % every === 0 && n - 1 - i >= every / 2 ? shortDay(d.date) : '',
    marker: d.pattern !== null,
    tip: (
      <>
        <div className="tt">{dayDate(d.date)} · No. {d.edition}</div>
        <div className="ts">{d.pattern ? `${d.pattern} pattern` : 'Per-row burns'}</div>
        <TipRow value={fmt(d.players)} label="played" />
        <TipRow color={S1} value={fmt(d.players - d.firstTimers)} label="returning" />
        <TipRow color={S2} value={fmt(d.firstTimers)} label="first game ever" />
        <TipRow value={fmt(d.finished)} label="finished" />
        <TipRow value={fmt(d.newPlayers)} label="new visitors" />
        <TipRow value={fmt(d.archive)} label="archive games" />
      </>
    ),
  }))
  return (
    <>
      <div className="an-legend">
        <span><i className="sw" style={{ background: S1 }} />Returning</span>
        <span><i className="sw" style={{ background: S2 }} />First game ever</span>
        <span><i className="dia" />Pattern day</span>
      </div>
      <Columns cols={cols} series={[S1, S2]} height={230} />
      <details className="an-more">
        <summary>Table</summary>
        <div className="an-table-wrap">
          <table className="an-table">
            <thead><tr><th>Day</th><th>No.</th><th className="l">Burns</th><th>Played</th><th>Returning</th><th>First game</th><th>Finished</th><th>New<br />visitors</th><th>Archive<br />games</th></tr></thead>
            <tbody>
              {[...a.daily].reverse().map((d) => (
                <tr key={d.date}>
                  <td>{dayDate(d.date)}</td><td>{d.edition}</td><td className="l">{d.pattern ?? 'per-row'}</td><td>{fmt(d.players)}</td><td>{fmt(d.players - d.firstTimers)}</td>
                  <td>{fmt(d.firstTimers)}</td><td>{fmt(d.finished)}</td><td>{fmt(d.newPlayers)}</td><td>{fmt(d.archive)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  )
}

function Hours({ a }: { a: AdminAnalytics }) {
  const total = a.hours.reduce((s, n) => s + n, 0)
  const name = (h: number) => `${h % 12 || 12}${h < 12 ? 'am' : 'pm'}`
  const cols: Column[] = a.hours.map((n, h) => ({
    key: String(h),
    values: [n],
    label: h % 6 === 0 ? name(h) : '',
    tip: (
      <>
        <div className="tt">{name(h)} to {name((h + 1) % 24)}</div>
        <TipRow color={S1} value={fmt(n)} label={`games · ${pct(ratio(n, total))}`} />
      </>
    ),
  }))
  return <Columns cols={cols} series={[S1]} height={180} />
}

// ---------- bars ----------

function Bars({ rows }: { rows: readonly { label: ReactNode; value: number; of: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value))
  return (
    <div className="hbars">
      {rows.map((r, i) => (
        <div className="hbar" key={i}>
          <span className="ellipsis">{r.label}</span>
          <span className="tr"><span className="fl" style={{ width: `${(r.value / max) * 100}%` }} /></span>
          <span className="v">{fmt(r.value)}<small>{pct(ratio(r.value, r.of))}</small></span>
        </div>
      ))}
    </div>
  )
}

function Ranks({ a }: { a: AdminAnalytics }) {
  const counts = RANK_STEPS.map((_, i) => a.editions.reduce((s, e) => s + e.ranks[i], 0))
  const total = counts.reduce((s, n) => s + n, 0)
  if (!total) return <p className="muted small">Nobody has finished a ranked game in this window.</p>
  return <Bars rows={RANK_STEPS.map((r, i) => ({ label: r.name, value: counts[i], of: total })).reverse()} />
}

function Devices({ a }: { a: AdminAnalytics }) {
  const d = a.devices
  const total = d.phone + d.tablet + d.desktop + d.unknown
  if (!total) return <p className="muted small">Nobody played in this window.</p>
  return (
    <Bars rows={[
      { label: 'Phone', value: d.phone, of: total },
      { label: 'Desktop', value: d.desktop, of: total },
      { label: 'Tablet', value: d.tablet, of: total },
      ...(d.unknown ? [{ label: 'Unknown', value: d.unknown, of: total }] : []),
    ]} />
  )
}

// ---------- tables ----------

function MiniBurns({ burns }: { burns: readonly (readonly [number, number])[] }) {
  const on = new Set(burns.map(([r, c]) => r * 5 + c))
  return <span className="burnmap xs" aria-hidden="true">{Array.from({ length: 30 }, (_, i) => <i key={i} className={on.has(i) ? 'b' : ''} />)}</span>
}

/** A pattern as its newest edition in the window laid it out: editions can mirror or flip the shape. */
const patternBurns = (a: AdminAnalytics, name: string) => a.editions.find((e) => e.pattern === name)?.burns ?? []

const Share = ({ n, of }: { n: number; of: number }) => <>{fmt(n)}<small>{pct(ratio(n, of))}</small></>

function Layouts({ a }: { a: AdminAnalytics }) {
  const first = PATTERN_DAYS_FROM
  return (
    <>
      <div className="an-table-wrap">
        <table className="an-table">
          <thead><tr><th>Layout</th><th>Days</th><th>Avg solver’s<br />best</th><th>Played</th><th>Finished</th><th>Median % of<br />solver’s best</th><th>Phoenix<br />raised</th><th>Ended before<br />row 6</th></tr></thead>
          <tbody>
            {a.layouts.map((l) => (
              <tr key={`${l.layout}|${l.pattern}`} className={l.pattern ? 'sub' : 'head'}>
                <td>{l.pattern
                  ? <span className="pat"><MiniBurns burns={patternBurns(a, l.pattern)} />{l.pattern}</span>
                  : <b>{l.layout === 'rows' ? 'Per-row days' : 'Pattern days'}</b>}</td>
                <td>{fmt(l.editions)}</td>
                <td>{fmt(l.averageBest)}</td>
                <td>{fmt(l.started)}</td>
                <td><Share n={l.finished} of={l.started} /></td>
                <td>{pct(l.medianShare)}</td>
                <td><Share n={l.phoenix} of={l.finished} /></td>
                <td><Share n={l.early} of={l.finished} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!a.layouts.some((l) => l.layout === 'pattern') && (
        <p className="faint small an-note">No pattern days in this window yet. They start with No. {first} on {dayDate(editionDate(first))} and come every other day.</p>
      )}
    </>
  )
}

function Editions({ a }: { a: AdminAnalytics }) {
  const [all, setAll] = useState(false)
  if (!a.editions.length) return <p className="muted small">No editions in this window.</p>
  return (
    <div className="an-table-wrap">
      <table className="an-table">
        <thead>
          <tr>
            <th>No.</th><th className="l">Day</th><th className="l">Burns</th><th>Best<br />score</th><th>Solver’s<br />best</th><th>Played</th><th>Finished</th>
            <th>Median<br />score</th><th>Median % of<br />solver’s best</th><th className="l">Ranks</th><th>Phoenix<br />raised</th><th>Ended before<br />row 6</th>
            <th>Median<br />time</th><th>Archive<br />games</th><th>Comments</th>
          </tr>
        </thead>
        <tbody>
          {(all ? a.editions : a.editions.slice(0, ROWS_SHOWN)).map((e) => {
            const top = Math.max(1, ...e.ranks)
            return (
              <tr key={e.number}>
                <td><b>{e.number}</b></td>
                <td className="l">{dayDate(e.date)}</td>
                <td className="l"><span className="pat"><MiniBurns burns={e.burns} />{e.pattern ?? <span className="faint">per-row</span>}</span></td>
                <td>{e.boardBest === null ? '—' : fmt(e.boardBest)}</td>
                <td>{fmt(e.best)}</td>
                <td>{fmt(e.started)}</td>
                <td><Share n={e.finished} of={e.started} /></td>
                <td>{e.medianTotal === null ? '—' : fmt(e.medianTotal)}</td>
                <td>{pct(e.medianShare)}</td>
                <td className="l">
                  <span className="an-hist" title={RANK_STEPS.map((r, i) => `${r.name} ${e.ranks[i]}`).join(' · ')} aria-label={RANK_STEPS.map((r, i) => `${r.name} ${e.ranks[i]}`).join(', ')}>
                    {e.ranks.map((n, i) => <i key={i} className={n ? '' : 'z'} style={{ height: n ? `${Math.max(12, (n / top) * 100)}%` : undefined }} />)}
                  </span>
                </td>
                <td><Share n={e.phoenix} of={e.finished} /></td>
                <td><Share n={e.early} of={e.finished} /></td>
                <td>{e.medianMinutes === null ? '—' : `${e.medianMinutes < 10 ? e.medianMinutes.toFixed(1) : Math.round(e.medianMinutes)}m`}</td>
                <td>{fmt(e.archive)}</td>
                <td>{fmt(e.comments)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <ShowAll all={all} set={setAll} total={a.editions.length} noun="editions" />
    </div>
  )
}

/** Rows a long table shows before "Show all". */
export const ROWS_SHOWN = 14

export function ShowAll({ all, set, total, noun }: { all: boolean; set(all: boolean): void; total: number; noun: string }) {
  if (total <= ROWS_SHOWN) return null
  return <button type="button" className="an-toggle" onClick={() => set(!all)}>{all ? `Show the latest ${ROWS_SHOWN}` : `Show all ${total} ${noun}`}</button>
}

function Retention({ a }: { a: AdminAnalytics }) {
  const [all, setAll] = useState(false)
  if (!a.retention.length) return <p className="muted small">Nobody played their first game in this window.</p>
  // All cohorts together, each day counting only cohorts that have reached it.
  const overall = RETENTION_OFFSETS.map((_, k) => {
    const reached = a.retention.filter((c) => c.returned[k] !== null)
    const players = reached.reduce((s, c) => s + c.players, 0)
    return players ? { n: reached.reduce((s, c) => s + c.returned[k]!, 0), of: players } : null
  })
  const cell = (n: number | null, of: number, key: number) => {
    if (n === null) return <td key={key} className="c" />
    const share = n / of
    const style = { background: `color-mix(in oklab, var(--s1) ${Math.round(share * 90)}%, var(--surface))`, color: share >= 0.5 ? '#fff' : undefined }
    return <td key={key} className="c" style={style} title={`${fmt(n)} of ${fmt(of)}`}>{pct(share)}</td>
  }
  return (
    <div className="an-table-wrap">
      <table className="an-table heat">
        <thead><tr><th>First game</th><th>Players</th>{RETENTION_OFFSETS.map((k) => <th key={k} className="c">Day {k}</th>)}</tr></thead>
        <tbody>
          <tr className="head">
            <td><b>All</b></td>
            <td>{fmt(a.retention.reduce((s, c) => s + c.players, 0))}</td>
            {overall.map((x, k) => (x ? cell(x.n, x.of, k) : <td key={k} className="c" />))}
          </tr>
          {(all ? a.retention : a.retention.slice(0, ROWS_SHOWN)).map((c) => (
            <tr key={c.date}>
              <td>{dayDate(c.date)}</td>
              <td>{fmt(c.players)}</td>
              {c.returned.map((n, k) => cell(n, c.players, k))}
            </tr>
          ))}
        </tbody>
      </table>
      <ShowAll all={all} set={setAll} total={a.retention.length} noun="days" />
    </div>
  )
}

function Words({ a }: { a: AdminAnalytics }) {
  const list = (title: string, words: readonly WordCount[]) => (
    <div>
      <div className="eyebrow">{title}</div>
      {words.length === 0 ? <p className="faint small">None yet.</p> : (
        <ol>
          {words.map((w, i) => <li key={w.word}><span className="n">{i + 1}</span><span>{w.word}</span><span className="c">{fmt(w.count)}</span></li>)}
        </ol>
      )}
    </div>
  )
  return (
    <div className="an-words">
      {list('Openers', a.words.openers)}
      {list('Any row', a.words.all)}
      {list('Phoenixes', a.words.phoenixes)}
    </div>
  )
}
